import { useRouter } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import { useUserStore } from '@/authentication/user.store'

export const UNAUTHORIZED = 'Unauthorized'

type Navigation = 'push' | 'replace'

// Content saved by one user must only open for that user. The check waits for a pending /current_user
// request, so a page can call it as soon as its content loads.
export function useRedirectIfUnauthorized() {
  const router = useRouter()
  const userStore = useUserStore()

  function isSignedInAs(userUuid: string): boolean {
    return userStore.isAuthenticated && userStore.userId === userUuid
  }

  function redirect(redirectRoute: RouteLocationRaw | string, navigation: Navigation) {
    if (typeof redirectRoute === 'string') {
      window.location.href = redirectRoute
      return
    }
    router[navigation](redirectRoute)
  }

  function redirectIfUnauthorized(userUuidToAuthorize: string, redirectRoute: RouteLocationRaw | string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!userUuidToAuthorize) {
        resolve()
        return
      }
      const checkSignedInUser = (navigation: Navigation) => {
        if (isSignedInAs(userUuidToAuthorize)) {
          resolve()
          return
        }
        redirect(redirectRoute, navigation)
        reject(UNAUTHORIZED)
      }
      // Vue 2 pushed after waiting for the user but replaced when the user was already known; kept as is.
      if (userStore.fetching) {
        userStore.pushOnetimeCallback(() => checkSignedInUser('push'))
        return
      }
      checkSignedInUser('replace')
    })
  }

  return { redirectIfUnauthorized }
}
